import { useSyncExternalStore } from "react";

import {
  BASE_UI_CONFIG,
  getUiConfigOverlay,
  subscribeToUiConfigOverlay,
  updateUiConfigOverlay,
} from "~/lib/ui-config";
import {
  preserveUiConfigLanguageOverlayFields,
  readUiConfigOverlayLanguageLocale,
} from "~/lib/ui-config/language-config";

export interface LanguageRegistryEntry {
  readonly flag: string;
  readonly formattingLocale: string;
  readonly label: string;
  readonly nativeLabel: string;
}

function defineLanguageRegistry<const Locale extends string>(
  registry: Readonly<Record<Locale, LanguageRegistryEntry>>,
): Readonly<Record<Locale, LanguageRegistryEntry>> {
  return registry;
}

export const LANGUAGE_REGISTRY = defineLanguageRegistry({
  en: {
    flag: "🇺🇸",
    formattingLocale: "en-US",
    label: "English",
    nativeLabel: "English",
  },
  de: {
    flag: "🇩🇪",
    formattingLocale: "de-DE",
    label: "German",
    nativeLabel: "Deutsch",
  },
  "pt-BR": {
    flag: "🇧🇷",
    formattingLocale: "pt-BR",
    label: "Brazilian Portuguese",
    nativeLabel: "Português (Brasil)",
  },
  tr: {
    flag: "🇹🇷",
    formattingLocale: "tr-TR",
    label: "Turkish",
    nativeLabel: "Türkçe",
  },
	"zh-CN": {
		flag: "🇨🇳",
		formattingLocale: "zh-CN",
		label: "Chinese (Mainland China)",
		nativeLabel: "简体中文（中国大陆）",
	},
	"zh-HK": {
		flag: "🇭🇰",
		formattingLocale: "zh-HK",
		label: "Chinese (Hong Kong)",
		nativeLabel: "繁體中文（香港）",
	},
	"ko-KR": {
		flag: "🇰🇷",
		formattingLocale: "ko-KR",
		label: "Korean (South Korea)",
		nativeLabel: "한국어 (대한민국)",
	},
	"ru-RU": {
		flag: "🇷🇺",
		formattingLocale: "ru-RU",
		label: "Russian",
		nativeLabel: "Русский",
	},
	"es-419": {
		flag: "🌎",
		formattingLocale: "es-MX",
		label: "Latin American Spanish",
		nativeLabel: "Español (Latinoamérica)",
	},
});

export type AppLocale = keyof typeof LANGUAGE_REGISTRY;

type LanguageLensListener = () => void;
type ActiveLocaleListener = () => void;

let urlLocaleOverride: AppLocale | null = null;
const activeLocaleListeners = new Set<ActiveLocaleListener>();

export function isAppLocale(locale: string): locale is AppLocale {
  return Object.hasOwn(LANGUAGE_REGISTRY, locale);
}

function normalizeLanguageTag(languageTag: string): string {
  return languageTag.trim().toLowerCase();
}

function matchChineseLanguageLocale(normalizedTag: string): AppLocale | null {
	const subtags = normalizedTag.split("-");
	if (subtags[0] !== "zh") {
		return null;
	}

	if (subtags.some((subtag) => ["hant", "hk", "mo", "tw"].includes(subtag))) {
		return "zh-HK";
	}

	return "zh-CN";
}

export function matchLanguageLocale(
  languageTags: readonly string[],
): AppLocale | null {
  for (const languageTag of languageTags) {
    const normalizedTag = normalizeLanguageTag(languageTag);
    const exactLocale = Object.keys(LANGUAGE_REGISTRY).find(
      (locale) => normalizeLanguageTag(locale) === normalizedTag,
    );
    if (exactLocale !== undefined && isAppLocale(exactLocale)) {
      return exactLocale;
    }

		const chineseLocale = matchChineseLanguageLocale(normalizedTag);
		if (chineseLocale !== null) {
			return chineseLocale;
		}

    const primarySubtag = normalizedTag.split("-")[0];
    if (!primarySubtag) {
      continue;
    }

    const primaryLocale = Object.keys(LANGUAGE_REGISTRY).find(
			(locale) => normalizeLanguageTag(locale).split("-")[0] === primarySubtag,
    );
    if (primaryLocale !== undefined && isAppLocale(primaryLocale)) {
      return primaryLocale;
    }
  }

  return null;
}

function getNavigatorLanguageLocale(): AppLocale | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  return matchLanguageLocale(navigator.languages);
}

export function getLanguageLocale(): AppLocale {
  const overlayLocale = readUiConfigOverlayLanguageLocale(getUiConfigOverlay());
  if (overlayLocale !== undefined && isAppLocale(overlayLocale)) {
    return overlayLocale;
  }

	return getNavigatorLanguageLocale() ?? BASE_UI_CONFIG.lenses.language.locale;
}

export function getUrlLocaleOverride(): AppLocale | null {
  return urlLocaleOverride;
}

export function setUrlLocaleOverride(locale: AppLocale | null): void {
  if (urlLocaleOverride === locale) {
    return;
  }

  urlLocaleOverride = locale;
  for (const listener of activeLocaleListeners) {
    listener();
  }
}

export function getActiveLocale(): AppLocale {
  return getUrlLocaleOverride() ?? getLanguageLocale();
}

export function setLanguageLocale(locale: AppLocale): void {
  const overlayLocale = readUiConfigOverlayLanguageLocale(getUiConfigOverlay());
  const alreadyPersisted =
    overlayLocale !== undefined &&
    isAppLocale(overlayLocale) &&
    overlayLocale === locale;

  if (!alreadyPersisted) {
    updateUiConfigOverlay((overlay) => ({
      ...overlay,
      lenses: {
        ...overlay.lenses,
        language: {
          ...preserveUiConfigLanguageOverlayFields(overlay.lenses?.language),
          locale,
        },
      },
    }));
  }

  // Explicit picker/runtime choices make the persisted lens authoritative.
  // Update the overlay first so active-locale consumers never observe the old
  // preference between removing the URL override and applying the choice.
  setUrlLocaleOverride(null);

  // A stale locale prefix left in browser history would re-apply the old
  // override on the router's next load (picker snap-back); the registered
  // sync rewrites the current history entry to match the new choice.
  localeUrlHistorySync?.();
}

type LocaleUrlHistorySync = () => void;
let localeUrlHistorySync: LocaleUrlHistorySync | null = null;

// Registered by the locale-url module (which imports from this module, so the
// dependency cannot point the other way without a cycle).
export function registerLocaleUrlHistorySync(fn: LocaleUrlHistorySync): void {
  localeUrlHistorySync = fn;
}

export function subscribeToLanguageLens(
  listener: LanguageLensListener,
): () => void {
  return subscribeToUiConfigOverlay(listener);
}

export function subscribeToActiveLocale(
  listener: ActiveLocaleListener,
): () => void {
  activeLocaleListeners.add(listener);
  const unsubscribeFromLanguageLens = subscribeToLanguageLens(listener);

  return () => {
    activeLocaleListeners.delete(listener);
    unsubscribeFromLanguageLens();
  };
}

export function useLanguageLocale(): AppLocale {
  return useSyncExternalStore(
    subscribeToLanguageLens,
    getLanguageLocale,
    () => BASE_UI_CONFIG.lenses.language.locale,
  );
}

export function useActiveLocale(): AppLocale {
  return useSyncExternalStore(
    subscribeToActiveLocale,
    getActiveLocale,
    () => BASE_UI_CONFIG.lenses.language.locale,
  );
}
